/**
 * Pre-computed waveform peaks.
 *
 * WaveSurfer only draws a real waveform from a decoded AudioBuffer. Its `MediaElement` /
 * `MediaElementWebAudio` backends never decode — `MediaElement.getPeaks()` returns `this.peaks || []`
 * — so a track played through an HTML5 media element renders flat unless peaks are supplied.
 *
 * We need the media element for playback because that is the only path that preserves pitch when the
 * speed slider is moved: `AudioBufferSourceNode.playbackRate` resamples (chipmunk effect), whereas a
 * media element time-stretches. So we decode here, once, and hand the peaks to WaveSurfer.
 *
 * This is the same decode the WebAudio backend already performed, just moved into our code — no extra
 * download and no extra decode.
 */

/** Interleaved [min, max, min, max, ...] — the format WaveSurfer expects for a single channel. */
export type Peaks = number[];

const cache = new Map<string, Promise<Peaks>>();

/**
 * How many files may be decoded at once.
 *
 * decodeAudioData on an uncompressed WAV is not free, and a folder has dozens of voicings. Run
 * unbounded, they all decode the moment their rows mount and the tab stops responding — which is
 * the several seconds a clip used to spend becoming playable. Drawing is never urgent: a waveform
 * that appears a moment late costs nothing, a clip that cannot sound on time costs the take.
 */
const MAX_CONCURRENT_DECODES = 2;

let running = 0;
const queue: Array<() => void> = [];

const acquire = (priority: boolean): Promise<void> =>
  new Promise((resolve) => {
    if (running < MAX_CONCURRENT_DECODES) {
      running += 1;
      resolve();
      return;
    }
    // The original track goes to the front. It is one decode against a folder's worth of cue
    // waveforms, and until it lands the track is redrawn when it finally does — which reloads the
    // element and seeks it. Behind a hundred cues that arrived tens of seconds into playback, and
    // the seek landed wherever the playhead had last been pointed: measured as the lecture jumping
    // from 74s to 342s mid-sentence.
    if (priority) queue.unshift(resolve);
    else queue.push(resolve);
  });

const release = (): void => {
  const next = queue.shift();
  if (next) {
    next();
    return;
  }
  running = Math.max(0, running - 1);
};

function pickAudioContext(): AudioContext | null {
  const Ctx =
    (window as any).AudioContext || (window as any).webkitAudioContext || null;
  return Ctx ? new Ctx() : null;
}

/**
 * Reduce a decoded buffer to `buckets` min/max pairs.
 *
 * Peaks are per rendered pixel, not per sample: a 3-hour lecture is ~300M samples, and building an
 * array anywhere near that size would cost more memory than the decode itself.
 */
function bucketPeaks(buffer: AudioBuffer, buckets: number): Peaks {
  const channels = buffer.numberOfChannels;
  const length = buffer.length;
  const step = Math.max(1, Math.floor(length / buckets));
  const peaks: Peaks = [];

  const data: Float32Array[] = [];
  for (let c = 0; c < channels; c += 1) {
    data.push(buffer.getChannelData(c));
  }

  for (let i = 0; i < buckets; i += 1) {
    const start = i * step;
    const end = Math.min(start + step, length);
    if (start >= length) {
      peaks.push(0, 0);
      continue;
    }
    let min = 0;
    let max = 0;
    for (let j = start; j < end; j += 1) {
      // Mix channels down so a mostly-mono lecture still shows its true envelope.
      let sample = 0;
      for (let c = 0; c < channels; c += 1) {
        sample += data[c][j];
      }
      sample /= channels;
      if (sample > max) max = sample;
      if (sample < min) min = sample;
    }
    peaks.push(min, max);
  }
  return peaks;
}

/**
 * Fetch + decode `url` and return interleaved min/max peaks.
 *
 * Resolves to an empty array on any failure — a missing waveform is a cosmetic degradation, and must
 * never stop the track from loading or playing.
 */
export async function computePeaks(
  url: string,
  buckets = 4096,
  signal?: AbortSignal,
  priority = false,
): Promise<Peaks> {
  if (!url) return [];

  const key = `${url}|${buckets}`;
  const cached = cache.get(key);
  if (cached) return cached;

  const task = (async (): Promise<Peaks> => {
    await acquire(priority);
    if (signal?.aborted) {
      release();
      cache.delete(key);
      return [];
    }
    const ctx = pickAudioContext();
    if (!ctx) {
      release();
      return [];
    }
    try {
      const response = await fetch(url, { signal });
      if (!response.ok) return [];
      const arrayBuffer = await response.arrayBuffer();
      const buffer = await ctx.decodeAudioData(arrayBuffer);
      return bucketPeaks(buffer, buckets);
    } catch (err) {
      // Do not cache a failure: an aborted or transient fetch should be retried on the next mount.
      cache.delete(key);
      return [];
    } finally {
      release();
      // Free the hardware context; decoding does not need it kept open.
      if (typeof ctx.close === "function") {
        ctx.close().catch(() => undefined);
      }
    }
  })();

  cache.set(key, task);
  return task;
}

/** Force pitch preservation on a media element (browsers default to true; older Safari is prefixed). */
export function forcePreservesPitch(media: HTMLMediaElement | null | undefined): void {
  if (!media) return;
  const el = media as any;
  el.preservesPitch = true;
  el.webkitPreservesPitch = true;
  el.mozPreservesPitch = true;
}
